import type {
  Response,
  FetchProduct,
  FetchCart,
  Product,
  FetchOrderSummary,
  CartItem,
  CartCookieItem,
  ProductOrder,
  ProductCategory,
} from "~/types";

interface CartActionResponse {
  cart_count: number;
  gross_total: string;
  total_price?: string;
}

export function useProducts() {
  const route = useRoute();
  const localePath = useLocalePath();

  const { t } = useI18n();
  const { apiVersion } = useRuntimeConfig().public;
  const { isLoggedIn } = useAuth();

  const {
    selectedLocation,
    selectedPaymentMethod,
    isNavigating,
    cartCount,
    cartTotal,
    payOrderLoading,
    productCategories,
    availableProductCategories,
    couponApplied,
    deal,
    orderSummaryDetails,
    giftCardCode,
    business,
    invoice,
  } = storeToRefs(useBusinessStore());
  const { isClientBlocked } = storeToRefs(useGlobalStore());

  const productDetails = ref<Product>();
  const cartItems = ref<CartItem[]>([]);
  const orderSummary = ref<FetchOrderSummary>();
  const productOrders = ref<ProductOrder[]>([]);
  const isUpdatingCart = ref(false);

  const cartCookie = useCookie<CartCookieItem[]>("cart", {
    default: () => [],
    maxAge: 60 * 60 * 24 * 7,
  });

  const prepareFormData = () => {
    const fd = new FormData();
    if (selectedPaymentMethod.value?.key === "in_store") {
      fd.append("payment_method_id", selectedPaymentMethod.value?.id.toString());
    } else if (selectedPaymentMethod.value?.key === "gift-card") {
      fd.append("gift_card_code", giftCardCode.value);
      fd.append(
        "payment_method_id",
        selectedPaymentMethod.value?.id.toString()
      );
    } else {
      fd.append(
        "payment_method_id",
        selectedPaymentMethod.value?.id.toString() || ""
      );
    }

    if (selectedPaymentMethod.value?.key === "tap-cc")
      fd.append("tap_token", selectedPaymentMethod.value?.tap_token as string);

    if (couponApplied.value && deal.value)
      fd.append("deal_id", deal.value.id.toString());

    if (selectedPaymentMethod.value?.accept_invoice && invoice.value)
      fd.append("invoice", invoice.value as File);

    return fd;
  };

  const updateCartTotals = () => {
    cartTotal.value = cartItems.value
      .reduce((sum, item) => {
        return sum + parseFloat(item.total_price);
      }, 0)
      .toFixed(3);

    cartCount.value = cartItems.value.reduce((sum, item) => {
      return sum + item.quantity;
    }, 0);
  };

  const goToLocations = async () =>
    await navigateTo(localePath(`/${selectedLocation.value?.identifier}`));

  const goToProducts = async () =>
    await navigateTo(
      localePath(`/${selectedLocation.value?.identifier}/products`)
    );

  const fetchProducts = async () => {
    if (!selectedLocation.value?.id) {
      return goToLocations();
    }

    const { data, error } = await useLarafetch<
      Response<{ product_categories: ProductCategory[] }>
    >(`/api/${apiVersion}/${selectedLocation.value?.identifier}/products`);

    /**
     * `useLarafetch` reports a failure on `error` rather than throwing, so the
     * `catch` that used to wrap this never ran: the catalogue kept whatever it
     * held and a failed request rendered as "this salon sells nothing".
     *
     * `$larafetch` has already surfaced the error, so all that is left is to
     * stop pretending the catalogue loaded — and only fail the page outright
     * when there is nothing already on screen to keep.
     */
    if (error.value) {
      if (!availableProductCategories.value?.length) {
        showError({ statusCode: httpErrorStatus(error.value) ?? 503 });
      }
      return;
    }

    productCategories.value = data.value?.data.product_categories ?? [];
    availableProductCategories.value = productCategories.value;
  };

  const fetchProduct = async () => {
    if (!selectedLocation.value?.id) {
      return goToProducts();
    }

    const { data, error } = await useLarafetch<Response<FetchProduct>>(
      `/api/${apiVersion}/${selectedLocation.value?.identifier}/products/${route.params.id}/details`
    );

    /**
     * The product page has nothing to show without this, and the `catch` it
     * used to rely on never fired — `useLarafetch` reports failures on
     * `error`. So a failed request left the previous product on screen, or an
     * empty page.
     */
    if (error.value || data.value?.code !== 200) {
      return showError({ statusCode: httpErrorStatus(error.value) ?? 503 });
    }

    productDetails.value = data.value.data.product;
  };

  const fetchCart = async () => {
    if (selectedLocation.value?.id) {
      if (isLoggedIn.value) {
        const { data, error } = await useLarafetch<Response<FetchCart>>(
          `/api/${apiVersion}/${selectedLocation.value?.identifier}/products/cart`
        );

        /**
         * The `catch` here never fired either, so a failed request left the
         * previous totals on screen — a stale cart, which matters more than a
         * stale list because the visitor is about to pay against it. Only fail
         * the page outright when there is nothing shown yet to keep.
         */
        if (error.value || data.value?.code !== 200) {
          if (!cartItems.value?.length) {
            showError({ statusCode: httpErrorStatus(error.value) ?? 503 });
          }
          return;
        }

        cartCount.value = data.value.data.cart.cart_count;
        cartTotal.value = data.value.data.cart.gross_total;
        cartItems.value = data.value.data.cart.products;
      } else {
        if (cartCookie.value) {
          await fetchProducts();

          const products = availableProductCategories.value.flatMap(
            (category) => category.products
          );

          cartItems.value = cartCookie.value
            .map((item) => {
              const product = products.find(
                (p) => p.id === item.product_location_id
              );
              if (!product) return null;

              const unitPrice = parseFloat(product.price);
              const totalPrice = (unitPrice * item.quantity).toFixed(business?.value?.currency_precision || 2);

              return {
                product_location_id: product.id,
                name: product.name,
                image: product.image,
                stock_quantity: product.quantity,
                quantity: item.quantity,
                unit_price: product.price,
                total_price: totalPrice,
              };
            })
            .filter(Boolean) as CartItem[];

          updateCartTotals();
        }
      }
    } else {
      return goToProducts();
    }
  };

  const fetchOrderSummary = async () => {
    if (selectedLocation.value?.id) {
      try {
        const { data, error } = await useLarafetch<Response<FetchOrderSummary>>(
          `/api/${apiVersion}/${selectedLocation.value?.identifier}/product-orders-summary`
        );

        if (data.value?.code === 200) {
          if (data.value?.data.product_orders.length === 0) {
            return goToProducts();
          }
          orderSummary.value = data.value.data;
          orderSummaryDetails.value = data.value.data;
          cartCount.value = data.value.data.cart_count;
          cartTotal.value = data.value.data.gross_total;
        } else if (error?.value?.data?.code === 1403) {
          isClientBlocked.value = true;
        } else if (error?.value && !orderSummary.value) {
          /**
           * Anything else used to fall through silently, leaving the checkout
           * summary to render with no order and no totals in it.
           */
          showError({ statusCode: httpErrorStatus(error.value) ?? 503 });
        }
      } catch (err: unknown) {
        console.error(err);
        return goToProducts();
      }
    } else {
      return goToProducts();
    }
  };

  const addToCart = async (product: Product, newQuantity: number) => {
    isUpdatingCart.value = true;
    if (isLoggedIn.value) {
      try {
        const response = await $larafetch<Response<CartActionResponse>>(
          `/api/${apiVersion}/${selectedLocation.value?.identifier}/products/${product.id}/add-cart`,
          {
            method: "POST",
            body: {
              quantity: newQuantity,
            },
          }
        );
        if (response?.code === 200) {
          cartCount.value = response.data.cart_count;
          cartTotal.value = response.data.gross_total;
          SuccessToast(response?.message);
        }
      } catch (err: unknown) {
        console.error(err);
      } finally {
        isUpdatingCart.value = false;
      }
    } else {
      const index = cartCookie.value?.findIndex(
        (item) => item.product_location_id === product.id
      );
      if (index !== -1) {
        if (
          product.quantity <=
          cartCookie.value[index].quantity + newQuantity
        ) {
          setTimeout(() => {
            ErrorToast(
              t("page.cart.stock_limit", { duration: product.quantity })
            );
            isUpdatingCart.value = false;
          }, 500);
          return;
        }
        cartCookie.value[index].quantity += newQuantity;
      } else {
        cartCookie.value.push({
          location_id: selectedLocation.value?.id,
          product_location_id: product.id,
          quantity: newQuantity,
        });
      }
      setTimeout(() => {
        SuccessToast(t("page.cart.item_added"));
        isUpdatingCart.value = false;
      }, 500);
    }
  };

  const updateCart = async (itemId: number, newQuantity: number) => {
    isUpdatingCart.value = true;
    if (isLoggedIn.value) {
      try {
        const response = await $larafetch<Response<CartActionResponse>>(
          `/api/${apiVersion}/${selectedLocation.value?.identifier}/products/${itemId}/update-cart`,
          {
            method: "POST",
            body: {
              quantity: newQuantity,
            },
          }
        );

        if (response?.code === 200) {
          cartCount.value = response.data.cart_count;
          cartTotal.value = response.data.gross_total;

          const updatedItem = cartItems.value.find(
            (item) => item.product_location_id === itemId
          );
          if (updatedItem && response.data.total_price) {
            updatedItem.total_price = response.data.total_price;
          }

          SuccessToast(response?.message);
        }
      } catch (error: unknown) {
        console.error("Error updating cart:", error);
      } finally {
        isUpdatingCart.value = false;
      }
    } else {
      const updatedCartItem = cartItems.value.find(
        (item) => item.product_location_id === itemId
      );
      if (updatedCartItem) {
        updatedCartItem.quantity = newQuantity;
        updatedCartItem.total_price = (
          parseFloat(updatedCartItem.unit_price) * newQuantity
        ).toFixed(business?.value?.currency_precision || 2);
      }

      const updatedCart = cartCookie.value.find(
        (item) => item.product_location_id === itemId
      );
      if (updatedCart) {
        updatedCart.quantity = newQuantity;
      }

      setTimeout(() => {
        SuccessToast(t("page.cart.item_updated"));
        isUpdatingCart.value = false;
      }, 500);

      updateCartTotals();
    }
  };

  const deleteCart = async (itemId: number) => {
    if (isLoggedIn.value) {
      try {
        const response = await $larafetch<Response<CartActionResponse>>(
          `/api/${apiVersion}/${selectedLocation.value?.identifier}/products/${itemId}/delete-cart`,
          {
            method: "DELETE",
          }
        );

        if (response?.code === 200) {
          cartCount.value = response.data.cart_count;
          cartTotal.value = response.data.gross_total;

          const index = cartItems.value.findIndex(
            (item) => item.product_location_id === itemId
          );
          if (index !== -1) cartItems.value.splice(index, 1);

          SuccessToast(response?.message);
        }
      } catch (err: unknown) {
        console.error(err);
      }
    } else {
      const CartItemsIndex = cartItems.value.findIndex(
        (item) => item.product_location_id === itemId
      );
      if (CartItemsIndex !== -1) cartItems.value.splice(CartItemsIndex, 1);

      const CartIndex = cartCookie.value.findIndex(
        (item) => item.product_location_id === itemId
      );
      if (CartIndex !== -1) cartCookie.value.splice(CartIndex, 1);

      updateCartTotals();
    }
  };

  const syncCart = async (items: CartCookieItem[]) => {
    const formData = new FormData();

    items.forEach((item, index) => {
      formData.append(`cart[${index}][quantity]`, String(item.quantity));
      formData.append(
        `cart[${index}][product_location_id]`,
        String(item.product_location_id)
      );
      if (item.location_id !== undefined) {
        formData.append(
          `cart[${index}][location_id]`,
          String(item.location_id)
        );
      }
    });

    try {
      const response = await $larafetch<Response<CartCookieItem[]>>(
        `/api/${apiVersion}/products/sync-cart`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response?.code === 200) {
        document.cookie =
          "cart=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      }
    } catch (err: unknown) {
      console.error(err);
    }
  };

  const payOrder = async () => {
    try {
      payOrderLoading.value = true;
      const response: Response<{
        redirect_url: string | null;
        product_order_id: string | number;
      }> = await $larafetch(
        `/api/${apiVersion}/${selectedLocation.value?.identifier}/new-product-order`,
        {
          method: "post",
          body: prepareFormData(),
        }
      );

      if (response.code !== 200) return;

      isNavigating.value = true;

      SuccessToast(response.message);

      if (selectedPaymentMethod.value?.redirect && response.data.redirect_url) {
        await navigateTo(response.data.redirect_url, { external: true });
        return;
      }

      await navigateTo(
        localePath(
          `/${selectedLocation.value?.identifier}/cart/finished?product_order_id=${response.data.product_order_id}`
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      payOrderLoading.value = false;
    }
  };

  return {
    productDetails,
    cartItems,
    orderSummary,
    productOrders,
    isUpdatingCart,
    fetchProducts,
    fetchProduct,
    fetchCart,
    fetchOrderSummary,
    addToCart,
    updateCart,
    deleteCart,
    syncCart,
    payOrder,
  };
}
